<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { updateImportSession, getLocations, getWarehouses, getAreas } from '@/api/api.js'
import MdiInformationOutline from '~icons/mdi/information-outline'
import MdiChevronRight from '~icons/mdi/chevron-right'
import MdiAlertOctagon from '~icons/mdi/alert-octagon'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'

const { t } = useI18n()

const props = defineProps({
  session: { type: Object, required: true }
})
const emit = defineEmits(['advance'])

// All importable fields with labels and metadata
const FIELDS = computed(() => [
  { key: 'name', label: t('import.fieldItemName'), required: true, type: 'text', group: 'Item' },
  { key: 'description', label: t('import.fieldDescription'), required: false, type: 'text', group: 'Item' },
  { key: 'category', label: t('import.fieldCategory'), required: false, type: 'text', group: 'Item' },
  {
    key: 'rental_price_per_day',
    label: t('import.fieldRentalPrice'),
    required: false,
    type: 'number',
    group: 'Item'
  },
  { key: 'weight_kg', label: t('import.fieldWeight'), required: false, type: 'number', group: 'Item' },
  { key: 'volume_m3', label: t('import.fieldVolume'), required: false, type: 'number', group: 'Item' },
  { key: 'is_container', label: t('import.isContainer'), required: false, type: 'boolean', group: 'Item' },
  // available_in_portal hidden — not yet implemented in portal
  // Metadata fields
  { key: 'asset_tag', label: t('import.fieldAssetTag'), required: false, type: 'text', group: 'Instance' },
  {
    key: 'serial_number',
    label: t('import.fieldSerialNumber'),
    required: false,
    type: 'text',
    group: 'Instance'
  },
  {
    key: 'purchase_price',
    label: t('import.fieldPurchasePrice'),
    required: false,
    type: 'number',
    group: 'Instance'
  },
  {
    key: 'purchase_date',
    label: t('import.fieldPurchaseDate'),
    required: false,
    type: 'date',
    group: 'Instance'
  },
  { key: 'status', label: t('import.fieldStatus'), required: false, type: 'select', group: 'Instance' },
  { key: 'comment', label: t('import.fieldComment'), required: false, type: 'text', group: 'Instance' },
  { key: 'area', label: t('import.fieldArea'), required: false, type: 'area', group: 'Instance' },
  { key: 'supplier', label: t('import.fieldSupplier'), required: false, type: 'text', group: 'Instance' },
  {
    key: 'available_quantity',
    label: t('import.fieldAvailableQuantity'),
    required: false,
    type: 'number',
    group: 'Instance'
  }
])

const STATUS_OPTIONS = computed(() => [
  { value: '1', label: t('instanceStatus.inService') },
  { value: '2', label: t('instanceStatus.defective') },
  { value: '3', label: t('instanceStatus.retired') },
])

const columns = computed(() => props.session.parsed_columns || [])

// Fuzzy name matching: auto-suggest column for field key
function autoSuggest(fieldKey) {
  const key = fieldKey.replace(/_/g, ' ').toLowerCase()
  const aliases = {
    asset_tag: ['asset tag', 'asset', 'tag', 'id', 'barcode'],
    name: ['name', 'item', 'product', 'equipment', 'description'],
    serial_number: ['serial', 'serial number', 'sn', 'serial no'],
    purchase_price: ['price', 'cost', 'purchase price', 'value'],
    purchase_date: ['date', 'purchase date', 'bought', 'acquired'],
    category: ['category', 'type', 'group', 'class'],
    area: ['area', 'location', 'zone', 'warehouse', 'storage', 'place'],
    supplier: ['supplier', 'vendor', 'manufacturer', 'brand'],
    available_quantity: ['quantity', 'qty', 'stock', 'count', 'amount'],
    comment: ['comment', 'note', 'notes', 'remarks', 'description'],
    status: ['status', 'condition', 'state'],
    weight_kg: ['weight', 'kg', 'mass'],
    volume_m3: ['volume', 'm3', 'cubic'],
    rental_price_per_day: ['rental', 'daily rate', 'rate', 'day'],
    is_container: ['container', 'is container']
  }

  const candidates = [key, ...(aliases[fieldKey] || [])]
  for (const col of columns.value) {
    const colLower = col.toLowerCase()
    if (candidates.some((c) => colLower === c || colLower.includes(c) || c.includes(colLower))) {
      return col
    }
  }
  return ''
}

// columnMapping: { fieldKey -> column_name | '' }
// defaults: { fieldKey -> default_value }
const columnMapping = ref({})
const defaults = ref({})
const saving = ref(false)
const saveError = ref('')
const referenceError = ref('')

// Cascading location → warehouse → area picker for global default
const locations = ref([])
const allWarehouses = ref([])
const allAreas = ref([])
const selectedLocationId = ref(null)
const selectedWarehouseId = ref(null)
const globalAreaId = ref(null)

const filteredWarehouses = computed(() =>
  selectedLocationId.value
    ? allWarehouses.value.filter((w) => w.location === selectedLocationId.value)
    : allWarehouses.value
)
const filteredAreas = computed(() =>
  selectedWarehouseId.value
    ? allAreas.value.filter((a) => a.warehouse === selectedWarehouseId.value)
    : []
)

// Reset downstream when parent selection changes
watch(selectedLocationId, () => {
  selectedWarehouseId.value = null
  globalAreaId.value = null
})
watch(selectedWarehouseId, () => {
  globalAreaId.value = null
})

async function loadReferences() {
  referenceError.value = ''
  try {
    const [locRes, whRes, areaRes] = await Promise.all([
      getLocations({ page_size: 500 }),
      getWarehouses({ page_size: 500 }),
      getAreas({ page_size: 500 })
    ])
    locations.value = locRes.results || (Array.isArray(locRes) ? locRes : [])
    allWarehouses.value = whRes.results || (Array.isArray(whRes) ? whRes : [])
    allAreas.value = areaRes.results || (Array.isArray(areaRes) ? areaRes : [])
  } catch {
    referenceError.value = t('import.referenceLoadError')
  }
}

onMounted(async () => {
  // Init from existing session data or auto-suggest
  FIELDS.value.forEach((f) => {
    columnMapping.value[f.key] = props.session.column_mapping?.[f.key] ?? autoSuggest(f.key)
    defaults.value[f.key] = props.session.defaults?.[f.key] ?? ''
  })

  await loadReferences()

  // Restore cascade from saved default area ID
  const savedAreaId = props.session.defaults?.area
  if (savedAreaId) {
    const area = allAreas.value.find((a) => a.id === Number(savedAreaId))
    if (area) {
      const wh = allWarehouses.value.find((w) => w.id === area.warehouse)
      if (wh) {
        selectedLocationId.value = wh.location
        await nextTick()
      }
      selectedWarehouseId.value = area.warehouse
      await nextTick()
      globalAreaId.value = area.id
    }
  }
})

const errors = computed(() => {
  const errs = {}
  FIELDS.value.filter((f) => f.required).forEach((f) => {
    if (!columnMapping.value[f.key] && !defaults.value[f.key]) {
      errs[f.key] = t('import.fieldRequired', { field: f.label })
    }
  })
  if (!selectedWarehouseId.value) {
    errs['_warehouse'] = t('import.warehouseRequired')
  }
  return errs
})

const hasErrors = computed(() => Object.keys(errors.value).length > 0)

const itemFields = computed(() => FIELDS.value.filter((f) => f.group === 'Item'))
const instanceFields = computed(() =>
  FIELDS.value.filter((f) => f.group === 'Instance' && f.key !== 'area')
)

async function advance() {
  if (hasErrors.value || referenceError.value) return
  saving.value = true
  saveError.value = ''
  try {
    const mappingToSend = {}
    Object.entries(columnMapping.value).forEach(([k, v]) => {
      if (v) mappingToSend[k] = v
    })
    const defaultsToSend = {}
    Object.entries(defaults.value).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) defaultsToSend[k] = v
    })
    // Save area global default as ID, and warehouse for auto-creating new areas
    if (!mappingToSend.area && globalAreaId.value) {
      defaultsToSend.area = globalAreaId.value
    }
    if (selectedWarehouseId.value) {
      defaultsToSend.warehouse_id = selectedWarehouseId.value
    }
    await updateImportSession(props.session.id, {
      column_mapping: mappingToSend,
      defaults: defaultsToSend,
      status: 'hierarchy'
    })
    emit('advance', { column_mapping: mappingToSend, defaults: defaultsToSend })
  } catch {
    saveError.value = t('import.saveMappingFailed')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-8">
    <p class="text-(--color-muted) text-sm">
      {{ $t('import.mappingIntro') }}
    </p>

    <div v-for="group in ['Item', 'Instance']" :key="group" class="flex flex-col gap-4">
      <h3 class="text-(--color-text) font-semibold text-sm uppercase tracking-wide opacity-60">
        {{ group === 'Item' ? $t('import.itemTemplateFields') : $t('import.instanceFields') }}
      </h3>

      <div
        v-for="field in group === 'Item' ? itemFields : instanceFields"
        :key="field.key"
        class="flex flex-col gap-1"
      >
        <div class="flex items-center gap-2">
          <span class="text-(--color-text) text-sm font-medium w-48 shrink-0">
            {{ field.label }}
            <span v-if="field.required" class="form-required">*</span>
          </span>

          <!-- Status field: select options -->
          <template v-if="field.type === 'select'">
            <BaseSelect v-model="columnMapping[field.key]" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMapped') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
            <BaseSelect
              v-if="!columnMapping[field.key]"
              v-model="defaults[field.key]"
              class="flex-1"
              density="compact"
            >
              <option value="">{{ $t('import.noDefault') }}</option>
              <option v-for="opt in STATUS_OPTIONS" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </BaseSelect>
          </template>

          <!-- Boolean field: pill toggle for default -->
          <template v-else-if="field.type === 'boolean'">
            <BaseSelect v-model="columnMapping[field.key]" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMappedUseDefault') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
            <button
              v-if="!columnMapping[field.key]"
              type="button"
              class="flex-1 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors cursor-pointer"
              :class="
                defaults[field.key] === 'true'
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                  : 'border-(--color-border) bg-(--color-surface-2) text-(--color-muted)'
              "
              @click="defaults[field.key] = defaults[field.key] === 'true' ? 'false' : 'true'"
            >
              <span
                class="w-2 h-2 rounded-full shrink-0"
                :class="defaults[field.key] === 'true' ? 'bg-emerald-500' : 'bg-(--color-border)'"
              />
              {{ defaults[field.key] === 'true' ? $t('common.yes') : $t('common.no') }}
            </button>
          </template>

          <!-- Number field -->
          <template v-else-if="field.type === 'number'">
            <BaseSelect v-model="columnMapping[field.key]" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMapped') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
            <BaseInput
              v-if="!columnMapping[field.key]"
              v-model="defaults[field.key]"
              type="number"
              step="any"
              density="compact"
              class="flex-1"
              :placeholder="$t('import.defaultOptional')"
            />
          </template>

          <!-- Date field -->
          <template v-else-if="field.type === 'date'">
            <BaseSelect v-model="columnMapping[field.key]" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMapped') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
            <BaseInput
              v-if="!columnMapping[field.key]"
              v-model="defaults[field.key]"
              type="date"
              density="compact"
              class="flex-1"
              :placeholder="$t('import.defaultOptional')"
            />
          </template>

          <!-- Text field (default) -->
          <template v-else>
            <BaseSelect v-model="columnMapping[field.key]" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMapped') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
            <BaseInput
              v-if="!columnMapping[field.key]"
              v-model="defaults[field.key]"
              type="text"
              density="compact"
              class="flex-1"
              :placeholder="field.required ? $t('import.defaultRequired') : $t('import.defaultOptional')"
            />
          </template>
        </div>

        <!-- Mapped column preview -->
        <p v-if="columnMapping[field.key]" class="text-(--color-accent) text-xs ml-48 pl-2">
          {{ $t('import.mappedFromColumn', { col: columnMapping[field.key] }) }}
        </p>

        <!-- Field hint -->
        <p v-if="field.hint" class="text-(--color-muted) text-xs ml-48 pl-2 italic">
          {{ field.hint }}
        </p>

        <!-- Validation error -->
        <p v-if="errors[field.key]" class="text-red-400 text-xs ml-48 pl-2 flex items-center gap-1">
          <MdiInformationOutline class="w-3 h-3 shrink-0" />
          {{ errors[field.key] }}
        </p>
      </div>
    </div>

    <!-- ── Location / Area assignment ──────────────────────────────── -->
    <div class="rounded-xl border border-(--color-border) bg-(--color-surface) overflow-hidden">
      <!-- Header -->
      <div class="px-4 py-3 bg-(--color-surface-2) border-b border-(--color-border)">
        <h3 class="text-(--color-text) font-semibold text-sm">{{ $t('import.locationAreaSection') }}</h3>
        <p class="text-(--color-muted) text-xs mt-0.5">
          {{ $t('import.locationAreaDesc') }}
        </p>
      </div>

      <div class="p-4 flex flex-col gap-4">
        <!-- Column mapping row -->
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <span class="text-(--color-text) text-sm font-medium w-48 shrink-0">{{ $t('import.columnInFile') }}</span>
            <BaseSelect v-model="columnMapping['area']" class="flex-1" density="compact">
              <option value="">{{ $t('import.notMappedApplyDefault') }}</option>
              <option v-for="col in columns" :key="col" :value="col">{{ col }}</option>
            </BaseSelect>
          </div>
          <p v-if="columnMapping['area']" class="text-(--color-accent) text-xs ml-48 pl-2">
            {{ $t('import.areaColumnInfo', { col: columnMapping['area'] }) }}
          </p>
        </div>

        <!-- Divider -->
        <div class="flex items-center gap-3">
          <div class="flex-1 border-t border-(--color-border)" />
          <span class="text-(--color-muted) text-xs">
              {{ columnMapping['area'] ? $t('import.fallbackWarehouse') : $t('import.defaultAreaForAll') }}
          </span>
          <div class="flex-1 border-t border-(--color-border)" />
        </div>

        <!-- Cascading picker: Location → Warehouse → Area -->
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <!-- Step 1: Location -->
          <div class="flex flex-col gap-1">
            <label class="text-(--color-muted) text-xs font-medium">{{ $t('import.step1Location') }}</label>
            <BaseSelect
              :model-value="selectedLocationId ?? ''"
              density="compact"
              @update:model-value="selectedLocationId = $event === '' ? null : Number($event)"
            >
              <option value="">{{ $t('import.selectDefault') }}</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">
                {{ loc.name }}
              </option>
            </BaseSelect>
          </div>

          <!-- Step 2: Warehouse -->
          <div class="flex flex-col gap-1">
            <label
              class="text-xs font-medium"
              :class="selectedLocationId ? 'text-(--color-muted)' : 'text-(--color-border)'"
              >{{ $t('import.step2Warehouse') }}</label
            >
            <BaseSelect
              :model-value="selectedWarehouseId ?? ''"
              :disabled="!selectedLocationId"
              density="compact"
              @update:model-value="selectedWarehouseId = $event === '' ? null : Number($event)"
            >
              <option value="">{{ $t('import.selectDefault') }}</option>
              <option v-for="wh in filteredWarehouses" :key="wh.id" :value="wh.id">
                {{ wh.name }}
              </option>
            </BaseSelect>
          </div>

          <!-- Step 3: Area -->
          <div class="flex flex-col gap-1">
            <label
              class="text-xs font-medium"
              :class="selectedWarehouseId ? 'text-(--color-muted)' : 'text-(--color-border)'"
              >{{ $t('import.step3Area') }}</label
            >
            <BaseSelect
              :model-value="globalAreaId ?? ''"
              :disabled="!selectedWarehouseId"
              density="compact"
              @update:model-value="globalAreaId = $event === '' ? null : Number($event)"
            >
              <option value="">{{ $t('import.noDefaultAreaOption') }}</option>
              <option v-for="area in filteredAreas" :key="area.id" :value="area.id">
                {{ area.name }}
              </option>
            </BaseSelect>
          </div>
        </div>

        <p v-if="globalAreaId" class="text-(--color-accent) text-xs">
          {{ $t('import.warehouseSelectedWithArea') }}
        </p>
        <p v-else-if="selectedWarehouseId" class="text-(--color-accent) text-xs">
          {{ $t('import.warehouseSelectedNoArea') }}
        </p>
        <p v-else class="text-red-400 text-xs font-medium flex items-start gap-1.5">
          <MdiAlertOctagon class="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>
            {{ $t('import.noWarehouseRequired') }}
          </span>
        </p>
      </div>
    </div>

    <div v-if="referenceError" class="flex items-center gap-2 text-red-400 text-sm" role="alert">
      {{ referenceError }}
      <button type="button" class="btn btn-ghost btn-small" @click="loadReferences">{{ $t('import.tryAgain') }}</button>
    </div>
    <p v-if="saveError" class="text-red-400 text-sm" role="alert">{{ saveError }}</p>

    <div class="flex justify-end">
      <button
        class="btn btn-primary flex items-center gap-2"
        :disabled="hasErrors || !!referenceError || saving"
        @click="advance"
      >
        {{ saving ? $t('common.saving') : $t('import.nextCategories') }}
        <MdiChevronRight class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>
